import { useLocalStorage } from '@vueuse/core';
import { useUserStore } from '@/stores/userStore';
import { useGameStore } from '@/stores/gameStore';
import FetchDataClient from '@/api/FetchDataClient';

// Debug helper (only logs in development)
const __isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
function debug(...args: unknown[]) {
  if (__isDev) console.debug('[GamesRefresh]', ...args);
}

interface GamesMeta {
  fetchedAt: number;
  etag: string | null;
  version: string | null;
}

interface ExtractedGames {
  games: Record<string, unknown>[] | null;
  in_mission?: boolean;
  mission_end?: number;
}

const TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const CACHE_VERSION =
  import.meta.env.VITE_GAMES_CACHE_VERSION ?? '2025-09-30-1';

function extractGamesFromResponse(
  data: Record<string, unknown>
): ExtractedGames {
  try {
    if (!data) return { games: null };

    // Direct array
    if (Array.isArray(data)) {
      return { games: data };
    }

    // Nested under games.games (AuthResponse-like)
    if (Array.isArray(data?.games?.games)) {
      return {
        games: data.games.games,
        in_mission: data.games.in_mission,
        mission_end: data.games.mission_end,
      };
    }

    // games is array at top-level
    if (Array.isArray(data?.games)) {
      return {
        games: data.games as Record<string, unknown>[],
        in_mission: data.in_mission as boolean | undefined,
        mission_end: data.mission_end as number | undefined,
      };
    }

    return { games: null };
  } catch {
    return { games: null };
  }
}

export function useGamesRefresh() {
  const gamesMeta = useLocalStorage<GamesMeta>('game.games_meta', {
    fetchedAt: 0,
    etag: null,
    version: null,
  });

  function ensureCacheVersionSync() {
    if (gamesMeta.value.version !== CACHE_VERSION) {
      debug('Cache version mismatch, forcing refresh', {
        previous: gamesMeta.value.version,
        next: CACHE_VERSION,
      });
      // Bump version and force a refresh by resetting fetchedAt
      gamesMeta.value = {
        ...gamesMeta.value,
        version: CACHE_VERSION,
        fetchedAt: 0,
      };
    }
  }

  async function refreshGamesIfStale({
    force = false,
  }: { force?: boolean } = {}) {
    const userStore = useUserStore();
    const gameStore = useGameStore();

    try {
      ensureCacheVersionSync();

      const ageMs = Date.now() - (gamesMeta.value.fetchedAt || 0);
      debug('refreshGamesIfStale called', {
        force,
        fetchedAt: gamesMeta.value.fetchedAt,
        ageMs,
        ttlMs: TTL_MS,
      });

      // TTL gate
      if (!force && ageMs < TTL_MS) {
        debug('Skipping refresh due to TTL window', { ageMs, ttlMs: TTL_MS });
        return;
      }

      // Require auth token for now (assuming protected endpoints)
      // In Pinia setup stores, getters are exposed as unwrapped values (not Refs)
      const token = userStore.getToken as string | null;
      if (!token) {
        debug('No auth token; skipping refresh');
        return;
      }

      // Create API client instance with token
      const api = new FetchDataClient(token);

      // Prefer the new dedicated endpoint, fallback to /user
      const endpoints = ['/games', '/user'];
      debug('Attempting refresh endpoints', endpoints);

      for (const endpoint of endpoints) {
        debug('Requesting endpoint', {
          endpoint,
          hasEtag: !!gamesMeta.value.etag,
          etag: gamesMeta.value.etag,
        });

        try {
          // Use FetchDataClient which handles the proper base URL
          const data = await api.get(endpoint, token);

          const { games, in_mission, mission_end } =
            extractGamesFromResponse(data);
          if (Array.isArray(games)) {
            debug('Received games payload', {
              endpoint,
              count: games.length,
              in_mission,
              mission_end,
            });
            // Update store
            gameStore.updateGames(games);
            if (typeof in_mission === 'boolean') {
              gameStore.setInMission(in_mission);
            }
            if (typeof mission_end === 'number') {
              gameStore.setMissionEnd(mission_end);
            }

            // Update meta
            gamesMeta.value.fetchedAt = Date.now();
            // Note: ETag support removed since FetchDataClient doesn't expose response headers
            // This is acceptable as we rely on TTL-based caching
            gamesMeta.value.etag = null;
            debug('Updated cache meta', {
              fetchedAt: gamesMeta.value.fetchedAt,
            });
            return;
          } else {
            debug('Endpoint returned no usable games payload', { endpoint });
          }
        } catch (endpointError) {
          debug('Endpoint request failed', { endpoint, error: endpointError });
          // Try next endpoint
          continue;
        }
      }

      // If neither endpoint returned usable games, exit quietly
      debug('No usable games payload from any endpoint');
    } catch (e) {
      // Swallow errors to avoid impacting UX; next focus/TTL will retry
      debug('refreshGamesIfStale error', e);
    }
  }

  function bindVisibilityRefetch() {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return () => {};

    debug('Binding visibilitychange revalidation');

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        debug('Tab visible: revalidating');
        refreshGamesIfStale();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    const unbind = () => {
      document.removeEventListener('visibilitychange', onVisible);
      debug('Unbound visibilitychange revalidation');
    };
    return unbind;
  }

  return {
    refreshGamesIfStale,
    ensureCacheVersionSync,
    bindVisibilityRefetch,
  };
}
